import { yupResolver } from "@hookform/resolvers/yup";
import { notification } from "antd";
import { useMemo } from "react";
import { useForm } from "react-hook-form";
import { useIntl } from "react-intl";

import {
  AgentAPI,
  ClientAPI,
  DictionaryAPI,
  HallAPI,
  TAgent,
  TClient,
  THall,
} from "@/api";
import ItemsQuery from "@/models/items-query";
import Query from "@/models/query";

import { PSPSettingProps } from "./form";
import { PSPSettingsSchema, usePSPSettingsSchema } from "./schema";

type QueryResponse<T> = Query<{ id: number }, T>;

export function usePSPSettingState({ entity }: PSPSettingProps) {
  const intl = useIntl();

  const schema = usePSPSettingsSchema();
  const form = useForm<PSPSettingsSchema>({
    resolver: yupResolver(schema),
  });

  const paymentGatewayQuery = useMemo(() => {
    const query = new ItemsQuery(DictionaryAPI.getPaymentGateways, {
      isSearchEnabled: false,
      isOrderEnabled: false,
      isPaginationEnabled: true,
    });
    query.submit({});
    return query;
  }, []);

  const entityId = useMemo(() => {
    if ("agentId" in entity) {
      return entity.agentId;
    }
    if ("clientId" in entity) {
      return entity.clientId;
    }
    return entity.hallId;
  }, [entity]);

  const query = useMemo(():
    | QueryResponse<TAgent>
    | QueryResponse<TClient>
    | QueryResponse<THall> => {
    let query:
      | QueryResponse<TAgent>
      | QueryResponse<TClient>
      | QueryResponse<THall>;

    if ("agentId" in entity) {
      query = new Query(AgentAPI.getById);
      query.submit({ id: entity.agentId }).then(() => {
        if (query.data) {
          const values = schema.cast(query.data, { stripUnknown: true });
          form.reset(values);
        }
      });
    } else if ("clientId" in entity) {
      query = new Query(ClientAPI.getById);
      query.submit({ id: entity.clientId }).then(() => {
        if (query.data) {
          const values = schema.cast(query.data, { stripUnknown: true });
          form.reset(values);
        }
      });
    } else {
      query = new Query(HallAPI.getById);
      query.submit({ id: entity.hallId }).then(() => {
        if (query.data) {
          const values = schema.cast(query.data, { stripUnknown: true });
          form.reset(values);
        }
      });
    }

    return query;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entity]);

  const submitQuery = useMemo(() => {
    return new Query(async (values: PSPSettingsSchema) => {
      const { paymentGateway, lobbySettings } = values;

      if ("agentId" in entity) {
        await AgentAPI.update({
          id: entity.agentId,
          lobbySettings,
          paymentGatewayId: paymentGateway?.id,
        });
      } else if ("clientId" in entity) {
        await ClientAPI.update({
          id: entity.clientId,
          lobbySettings,
          paymentGatewayId: paymentGateway?.id,
        });
      } else if ("hallId" in entity) {
        await HallAPI.update({
          id: entity.hallId,
          lobbySettings,
          paymentGatewayId: paymentGateway?.id,
        });
      }

      notification.success({
        message: "",
        description: intl.formatMessage({
          defaultMessage: "The lobby settings have been successfully updated.",
        }),
      });
      query.submit({ id: entityId }).then(() => {
        if (query.data) {
          const values = schema.cast(query.data, { stripUnknown: true });
          form.reset(values);
        }
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resetQuery = useMemo(() => {
    return new Query(async () => {
      if ("agentId" in entity) {
        await AgentAPI.update({
          id: entity.agentId,
          paymentGatewayId: null,
        });
      } else if ("clientId" in entity) {
        await ClientAPI.update({
          id: entity.clientId,
          paymentGatewayId: null,
        });
      } else if ("hallId" in entity) {
        await HallAPI.update({
          id: entity.hallId,
          paymentGatewayId: null,
        });
      }

      notification.success({
        message: "",
        description: intl.formatMessage({
          defaultMessage: "The lobby settings have been successfully updated.",
        }),
      });
      query.submit({ id: entityId }).then(() => {
        if (query.data) {
          const raw = schema.cast(query.data, { stripUnknown: true });

          const values = {
            ...raw,
            paymentGateway: query.data.paymentGateway
              ? raw.paymentGateway
              : null,
          };

          form.reset(values);
        }
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    intl,
    form,
    query,
    paymentGatewayQuery,
    submitQuery,
    resetQuery,
  };
}

export type PSPSettingState = ReturnType<typeof usePSPSettingState>;
